// @ts-nocheck
import { useState, useMemo } from 'react';
// eslint-disable-next-line ferp-subgraph-ui/no-direct-mantine-import
import {
  Stack,
  Paper,
  Group,
  Text,
  Button,
  Modal,
  TextInput,
  Textarea,
  Select,
  Checkbox,
  Badge,
  ActionIcon,
  Progress,
  Loader,
  Center,
  Alert,
} from '@mantine/core';
// eslint-disable-next-line ferp-subgraph-ui/no-direct-mantine-import
import { DateInput } from '@mantine/dates';
// eslint-disable-next-line ferp-subgraph-ui/no-direct-mantine-import
import { notifications } from '@mantine/notifications';
import {
  IconPlus,
  IconCheck,
  IconX,
  IconClipboardList,
} from '@tabler/icons-react';
import { useParams } from 'react-router-dom';
import { PageHeader } from '../../components/PageHeader';
import { StatusBadge } from '../../components/StatusBadge';
import {
  useOnboardingChecklist,
  useCompleteOnboardingItem,
  useSkipOnboardingItem,
  useAddOnboardingItem,
} from '../../hooks/useRecruitment';
import type {
  OnboardingItem,
  OnboardingItemCategory,
  OnboardingItemStatus,
  AddOnboardingItemInput,
} from '../../types/recruitment';

const CATEGORY_ORDER: OnboardingItemCategory[] = [
  'PAPERWORK',
  'IT_SETUP',
  'TRAINING',
  'INTRODUCTION',
  'EQUIPMENT',
  'POLICY_REVIEW',
  'OTHER',
];

const CATEGORY_LABELS: Record<OnboardingItemCategory, string> = {
  PAPERWORK: 'Paperwork',
  IT_SETUP: 'IT Setup',
  TRAINING: 'Training',
  INTRODUCTION: 'Introductions',
  EQUIPMENT: 'Equipment',
  POLICY_REVIEW: 'Policy Review',
  OTHER: 'Other',
};

const CATEGORY_COLORS: Record<OnboardingItemCategory, string> = {
  PAPERWORK: 'blue',
  IT_SETUP: 'cyan',
  TRAINING: 'violet',
  INTRODUCTION: 'green',
  EQUIPMENT: 'orange',
  POLICY_REVIEW: 'indigo',
  OTHER: 'gray',
};

const CATEGORY_OPTIONS = CATEGORY_ORDER.map((c) => ({
  value: c,
  label: CATEGORY_LABELS[c],
}));

export default function OnboardingDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [addItemModalOpen, setAddItemModalOpen] = useState(false);
  const [itemForm, setItemForm] = useState({
    title: '',
    description: '',
    category: 'OTHER' as OnboardingItemCategory,
    assignedTo: '',
    dueDate: null as string | null,
    required: true,
  });

  const { data: checklistRaw, isLoading, error } = useOnboardingChecklist(id!);
  const checklist = checklistRaw as any;
  const completeMutation = useCompleteOnboardingItem();
  const skipMutation = useSkipOnboardingItem();
  const addItemMutation = useAddOnboardingItem();

  // ── Group items by category ─────────────────────────────────────────
  const groupedItems = useMemo(() => {
    if (!checklist) return new Map<OnboardingItemCategory, OnboardingItem[]>();
    const grouped = new Map<OnboardingItemCategory, OnboardingItem[]>();
    for (const category of CATEGORY_ORDER) {
      const items = checklist.items.filter((item: OnboardingItem) => item.category === category);
      if (items.length > 0) {
        grouped.set(category, items);
      }
    }
    return grouped;
  }, [checklist]);

  const completionPercentage = checklist && checklist.totalCount > 0
    ? Math.round((checklist.completedCount / checklist.totalCount) * 100)
    : 0;

  const handleComplete = async (itemId: string) => {
    if (!id) return;
    try {
      await completeMutation.mutateAsync({ checklistId: id, itemId });
      notifications.show({ title: 'Completed', message: 'Item marked as complete', color: 'green' });
    } catch {
      notifications.show({ title: 'Error', message: 'Failed to complete item', color: 'red' });
    }
  };

  const handleSkip = async (itemId: string) => {
    if (!id) return;
    try {
      await skipMutation.mutateAsync({ checklistId: id, itemId });
      notifications.show({ title: 'Skipped', message: 'Item skipped', color: 'gray' });
    } catch {
      notifications.show({ title: 'Error', message: 'Failed to skip item', color: 'red' });
    }
  };

  const handleAddItem = async () => {
    if (!id || !itemForm.title) return;
    const input: AddOnboardingItemInput = {
      checklistId: id,
      title: itemForm.title,
      description: itemForm.description,
      category: itemForm.category,
      assignedTo: itemForm.assignedTo || undefined,
      dueDate: itemForm.dueDate ?? undefined,
      required: itemForm.required,
    };

    try {
      await addItemMutation.mutateAsync(input);
      notifications.show({ title: 'Added', message: 'Item added to checklist', color: 'green' });
      setAddItemModalOpen(false);
      setItemForm({
        title: '',
        description: '',
        category: 'OTHER',
        assignedTo: '',
        dueDate: null,
        required: true,
      });
    } catch {
      notifications.show({ title: 'Error', message: 'Failed to add item', color: 'red' });
    }
  };

  const getItemStatusColor = (status: OnboardingItemStatus): string => {
    switch (status) {
      case 'COMPLETED':
        return 'green';
      case 'IN_PROGRESS':
        return 'blue';
      case 'SKIPPED':
        return 'gray';
      default:
        return 'yellow';
    }
  };

  if (isLoading) {
    return (
      <Center py="xl">
        <Loader size="lg" />
      </Center>
    );
  }

  if (error || !checklist) {
    return (
      <Alert color="red" title="Error">
        Failed to load onboarding checklist.
      </Alert>
    );
  }

  return (
    <Stack gap="md">
      <PageHeader
        title={`Onboarding: ${checklist.employeeName}`}
        breadcrumbs={[
          { label: 'Onboarding', to: '/admin/workforce/onboarding' },
          { label: checklist.employeeName },
        ]}
        actions={
          <Button
            icon={<IconPlus size={16} />}
            onClick={() => setAddItemModalOpen(true)}
          >
            Add Item
          </Button>
        }
      />

      {/* Header Card */}
      <Paper withBorder p="lg" radius="md">
        <Group justify="space-between" ta="flex-start" mb="md">
          <Stack gap={4}>
            <Group gap="sm">
              <Text size="xl" fw={700}>
                {checklist.employeeName}
              </Text>
              <StatusBadge status={checklist.status} />
            </Group>
            <Group gap="xl">
              <Text size="sm">
                <Text span fw={500}>
                  Start:
                </Text>{' '}
                {new Date(checklist.startDate).toLocaleDateString()}
              </Text>
              <Text size="sm">
                <Text span fw={500}>
                  Target:
                </Text>{' '}
                {new Date(checklist.targetCompletionDate).toLocaleDateString()}
              </Text>
              <Text size="sm">
                <Text span fw={500}>
                  Items:
                </Text>{' '}
                {checklist.completedCount} / {checklist.totalCount}
              </Text>
            </Group>
          </Stack>
          <Text fw={700} fz={36} c={completionPercentage === 100 ? 'green' : 'blue'}>
            {completionPercentage}%
          </Text>
        </Group>
        <Progress
          value={completionPercentage}
          size="lg"
          color={completionPercentage === 100 ? 'green' : 'blue'}
          radius="md"
        />
      </Paper>

      {/* Items Grouped by Category */}
      {groupedItems.size === 0 ? (
        <Paper withBorder p="xl" ta="center">
          <IconClipboardList
            size={48}
            stroke={1.5}
            color="var(--mantine-color-dimmed)"
          />
          <Text c="dimmed" mt="sm">
            No items in this checklist yet. Add items to get started.
          </Text>
        </Paper>
      ) : (
        Array.from(groupedItems.entries()).map(([category, items]) => (
          <Paper key={category} withBorder p="md" radius="md">
            <Group gap="sm" mb="md">
              <Badge
                color={CATEGORY_COLORS[category]}
                variant="light"
                size="lg"
              >
                {CATEGORY_LABELS[category]}
              </Badge>
              <Text size="sm" c="dimmed">
                {items.filter((i) => i.status === 'COMPLETED').length}/{items.length} completed
              </Text>
            </Group>

            <Stack gap="xs">
              {items.map((item) => {
                const isDone = item.status === 'COMPLETED' || item.status === 'SKIPPED';
                const isOverdue =
                  item.dueDate &&
                  item.status !== 'COMPLETED' &&
                  item.status !== 'SKIPPED' &&
                  new Date(item.dueDate) < new Date();

                return (
                  <Paper
                    key={item.id}
                    withBorder
                    p="sm"
                    radius="sm"
                    bg={isDone ? 'gray.0' : undefined}
                    style={isOverdue ? { borderColor: 'var(--mantine-color-red-4)' } : undefined}
                  >
                    <Group justify="space-between" ta="flex-start" wrap="nowrap">
                      <Group gap="sm" ta="flex-start" wrap="nowrap" style={{ flex: 1 }}>
                        <Checkbox
                          checked={item.status === 'COMPLETED'}
                          disabled={isDone}
                          onChange={() => handleComplete(item.id)}
                          mt={2}
                        />
                        <Stack gap={2} style={{ flex: 1 }}>
                          <Group gap="xs">
                            <Text
                              size="sm"
                              fw={500}
                              td={isDone ? 'line-through' : undefined}
                              c={isDone ? 'dimmed' : undefined}
                            >
                              {item.title}
                            </Text>
                            {item.required && (
                              <Badge size="xs" color="red" variant="light">
                                Required
                              </Badge>
                            )}
                            {isOverdue && (
                              <Badge size="xs" color="red" variant="filled">
                                Overdue
                              </Badge>
                            )}
                          </Group>
                          {item.description && (
                            <Text size="xs" c="dimmed">
                              {item.description}
                            </Text>
                          )}
                          <Group gap="lg">
                            {item.assignedTo && (
                              <Text size="xs" c="dimmed">
                                Assigned to: {item.assignedTo}
                              </Text>
                            )}
                            {item.dueDate && (
                              <Text size="xs" c={isOverdue ? 'red' : 'dimmed'}>
                                Due: {new Date(item.dueDate).toLocaleDateString()}
                              </Text>
                            )}
                            {item.completedAt && (
                              <Text size="xs" c="green">
                                Completed: {new Date(item.completedAt).toLocaleDateString()}
                              </Text>
                            )}
                          </Group>
                        </Stack>
                      </Group>

                      <Group gap={4} wrap="nowrap">
                        <Badge
                          size="xs"
                          color={getItemStatusColor(item.status)}
                          variant="light"
                        >
                          {item.status.replace(/_/g, ' ')}
                        </Badge>
                        {!isDone && (
                          <>
                            <ActionIcon
                              variant="subtle"
                              color="green"
                              size="sm"
                              title="Mark Complete"
                              onClick={() => handleComplete(item.id)}
                            >
                              <IconCheck size={14} />
                            </ActionIcon>
                            {!item.required && (
                              <ActionIcon
                                variant="subtle"
                                color="gray"
                                size="sm"
                                title="Skip"
                                onClick={() => handleSkip(item.id)}
                              >
                                <IconX size={14} />
                              </ActionIcon>
                            )}
                          </>
                        )}
                      </Group>
                    </Group>
                  </Paper>
                );
              })}
            </Stack>
          </Paper>
        ))
      )}

      {/* Add Item Modal */}
      <Modal
        opened={addItemModalOpen}
        onClose={() => setAddItemModalOpen(false)}
        title="Add Onboarding Item"
        size="md"
      >
        <Stack gap="sm">
          <TextInput
            label="Title"
            required
            value={itemForm.title}
            onChange={(e) => setItemForm((f) => ({ ...f, title: e.currentTarget.value }))}
            placeholder="e.g. Complete I-9 form"
          />

          <Textarea
            label="Description"
            value={itemForm.description}
            onChange={(e) => setItemForm((f) => ({ ...f, description: e.currentTarget.value }))}
            placeholder="Additional details..."
            minRows={2}
          />

          <Select
            label="Category"
            required
            value={itemForm.category}
            onChange={(v) =>
              setItemForm((f) => ({
                ...f,
                category: (v as OnboardingItemCategory) || 'OTHER',
              }))
            }
            data={CATEGORY_OPTIONS}
          />

          <TextInput
            label="Assigned To"
            value={itemForm.assignedTo}
            onChange={(e) => setItemForm((f) => ({ ...f, assignedTo: e.currentTarget.value }))}
            placeholder="Person or team responsible"
          />

          <DateInput
            label="Due Date"
            value={itemForm.dueDate}
            onChange={(v) => setItemForm((f) => ({ ...f, dueDate: v }))}
            placeholder="Optional"
            clearable
          />

          <Checkbox
            label="Required item"
            checked={itemForm.required}
            onChange={(e) => setItemForm((f) => ({ ...f, required: e.currentTarget.checked }))}
          />

          <Group justify="flex-end" mt="md">
            <Button variant="default" onClick={() => setAddItemModalOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleAddItem}
              loading={addItemMutation.isPending}
              disabled={!itemForm.title}
            >
              Add Item
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
